// Målpeking i kameraet («target toggle») — eierens idé 2026-08-17: pek på
// objektet i kamerabildet (en båt langs kysten), få avstand + et lite
// kartvindu. Se `vale/MAALPEKING_KONSEPT_2026-08-17.md` for HELE grunnlaget.
//
// TO NYE METODER, INGEN AV DEM FINNES I ATAK (konseptdok funn 1):
//   • MARITIM (horisont+havflate): klikk horisontlinja OG målets vannlinje i
//     SAMME bilde → avstand, analytisk, INGEN høydemodell. `pitch/roll`
//     kanselleres fordi begge klikk leses fra samme kalibrering (se
//     `gapDegFraPiksler` i @skade/contracts).
//   • LANDEMERKE-RESECTION: klikk på ett eller flere KJENTE punkt i bildet
//     (fyrlykt, mast, kai) → boresight-asimut <0,1° med tele, sammenlignet
//     EKSPLISITT mot magnetometerets ±20° (konseptdok funn 2).
//
// ⚠ GRUNNLOVEN §1, ORDRETT: resultatpanelet viser ALLTID avstand+σ+metode OG
// asimut+σ+metode hver for seg — ALDRI en bar posisjon. Er bare ÉN av de to
// kjent, tegnes en SEKTOR (samme prinsipp som `maalestimat.ts` sin
// «ingen-treff» → sektor), ikke et punkt vi ikke har dekning for.
//
// ⚠ IKKE BILDEGJENKJENNING FOR LANDEMERKER. De identifiseres MANUELT av
// operatøren (klikk + kjent posisjon tastet inn) — automatisk feature-matching
// finnes ikke i denne kodebasen og bygges ikke her (se rapporten til bølge
// MAAL-01).
//
// STIL: samme oppdeling som `sikt.ts` (ren tilstand + DOM-lag) og samme
// bilde-overlegg som `imint.ts` (ImintAnnotator) — ingen ny avhengighet.
//
// ⚠ REVERSERING (kort MAAL-03, §4 krever at slike står eksplisitt): filhodet
// sa tidligere «ingen canvas-piksellesing». Det holdt så lenge horisonten bare
// ble MERKET av operatøren. MAAL-03 legger til en VALGFRI auto-deteksjon
// (`autoDetekterHorisont`) som leser bildets faktiske piksler via en
// frakoblet <canvas> for å FINNE horisontlinja automatisk — bak en egen knapp
// operatøren selv trykker, ikke noe som kjører stille. Selve algoritmen
// (`detekterHorisontLinje`/`pitchRollFraHorisontlinje`) er DOM-fri og bor i
// @skade/contracts (`horisontdeteksjon.ts`); canvas-en her er bare adapteren
// som henter pikslene ut av <img>.
import type { Map as MlMap, GeoJSONSource } from 'maplibre-gl';
import {
  featureCollection, project, sektorPolygon, treffusikkerhetRing,
  gapDegFraPiksler, maritimAvstand, losResection, resectionPeilingTilMaal, fusjonerMaalpeking,
  peilingFraPiksel, kameraSektorHalvbreddeDeg,
  detekterHorisontLinje, pitchRollFraHorisontlinje,
  MAGNETOMETER_SIGMA_DEG_DEFAULT, KAMERA_REKKEVIDDE_DEFAULT_M,
  type GeoFeature, type LatLon, type Kamerakalibrering,
  type MaritimtResultat, type ResectionResultat, type Maalpeking, type AsimutKomponent, type AvstandKomponent,
  type LandemerkeObservasjon, type Pikselrute, type PitchRollResultat,
} from '@skade/contracts';
import { sporTekst, trygtBildeUrl } from './shell.js';

// ── Tilstand (rent, DOM-fritt — derfor testbart uten jsdom) ─────────────────

export type MalpekingKlikkModus = 'ingen' | 'horisont' | 'mal' | 'landemerke';

/** Ett landemerke i lista, med et menneskelesbart navn og løst av-fra-piksel. */
export interface MalpekingLandemerkeRad extends LandemerkeObservasjon {
  /** Lokal id for lista (rekkefølge, sletting) — ikke domenedata. */
  id: number;
}

export interface MalpekingBeregning {
  avstand: MaritimtResultat | null;
  resection: ResectionResultat | null;
  /** Kun satt når BÅDE asimut og avstand er kjent — se filhodets §1-merknad. */
  maal: Maalpeking | null;
  /** Satt når asimut er kjent men avstand ikke er det — vises som sektor. */
  sektor: { asimutDeg: number; halvbreddeDeg: number; metode: string; forklaring: string } | null;
  advarsler: string[];
}

/**
 * All tilstand for målpekingspanelet, DOM-fri. Speiler `SiktTilstand` sin
 * rolle i `sikt.ts`: alt som kan testes uten en nettleser, testes her.
 */
export class MalpekingTilstand {
  bildeUrl = '';
  /** Fra `img.naturalWidth/Height` når bildet er lastet — ikke tastet inn manuelt. */
  breddePx = 0;
  hoydePx = 0;
  hfovDeg = 60;
  vfovDeg: number | undefined = undefined;

  kameraLat = 0;
  kameraLon = 0;
  /**
   * Kameraets monterte/antatte asimut (grader sann). Dette er FALLBACKEN når
   * ingen landemerker er identifisert — typisk fra telefonens magnetometer
   * (deklinasjonskorrigert). σ er halve synsfeltet med mindre en σ er oppgitt
   * eksplisitt (`kameraSektorHalvbreddeDeg`-mønsteret fra kamerakalibrering.ts).
   */
  kameraAsimutDeg: number | undefined = undefined;
  kameraAsimutSigmaDeg: number | undefined = undefined;

  bakkehoydeM = 0;
  vannstandM = 0;
  holdehoydeM = 1.6;
  bolgehoydeSignifikantM: number | undefined = undefined;

  horisontYPiksel: number | null = null;
  malXPiksel: number | null = null;
  malYPiksel: number | null = null;
  /**
   * Pitch/roll fra siste AUTO-DETEKTERTE horisontlinje — `null` når horisonten
   * er satt manuelt (klikk) eller ikke satt i det hele tatt. Vises til
   * operatøren som diagnostikk: dette er tallet som er langt bedre enn
   * telefonens IMU-pitch (1,7° feil, se maalestimat.ts), og som er KLART for
   * å erstatte IMU-pitchen (`sensorposeMedHorisontPitch` i @skade/contracts)
   * den dagen et kallsted i denne kodebasen faktisk trenger det — se
   * kommentaren i horisontdeteksjon.ts for hvorfor det ikke finnes i dag.
   */
  horisontAuto: PitchRollResultat | null = null;
  landemerker: MalpekingLandemerkeRad[] = [];
  /** Pikselkolonnen et pågående «legg til landemerke»-klikk satte, i påvente av navn/lat/lon. */
  ventendeLandemerkeXPiksel: number | null = null;

  modus: MalpekingKlikkModus = 'ingen';
  private nesteLandemerkeId = 1;

  /** Kalibreringen slik resten av @skade/contracts kjenner den (gjenbruk, ikke ny form). */
  kalibrering(): Kamerakalibrering {
    return {
      kameraId: 'malpeking-panel',
      lat: this.kameraLat,
      lon: this.kameraLon,
      asimutDeg: this.kameraAsimutDeg ?? 0,
      hfovDeg: this.hfovDeg,
      vfovDeg: this.vfovDeg,
      breddePx: Math.max(1, this.breddePx),
      hoydePx: this.hoydePx || undefined,
      asimutSigmaDeg: this.kameraAsimutSigmaDeg,
    };
  }

  /** Klikk i bildet, i NORMALISERTE koordinater (0–1), fra overlegget. */
  settKlikkNormalisert(xNorm: number, yNorm: number): void {
    if (!this.breddePx || !this.hoydePx) return; // bildet ikke lastet enda
    const x = xNorm * this.breddePx;
    const y = yNorm * this.hoydePx;
    if (this.modus === 'horisont') {
      this.horisontYPiksel = y;
      this.horisontAuto = null; // et manuelt klikk overstyrer en tidligere auto-deteksjon
    } else if (this.modus === 'mal') {
      this.malXPiksel = x;
      this.malYPiksel = y;
    } else if (this.modus === 'landemerke') {
      this.ventendeLandemerkeXPiksel = x;
    }
    this.modus = 'ingen';
  }

  leggTilLandemerke(navn: string, lat: number, lon: number): void {
    if (this.ventendeLandemerkeXPiksel === null) return;
    this.landemerker.push({
      id: this.nesteLandemerkeId++,
      navn, lat, lon,
      xPiksel: this.ventendeLandemerkeXPiksel,
    });
    this.ventendeLandemerkeXPiksel = null;
  }

  fjernLandemerke(id: number): void {
    this.landemerker = this.landemerker.filter((l) => l.id !== id);
  }

  nullstill(): void {
    this.horisontYPiksel = null;
    this.horisontAuto = null;
    this.malXPiksel = null;
    this.malYPiksel = null;
    this.landemerker = [];
    this.ventendeLandemerkeXPiksel = null;
    this.modus = 'ingen';
  }

  /**
   * Finn horisontlinja AUTOMATISK i pikslene (kort MAAL-03), i stedet for at
   * operatøren klikker den. Fyller SAMME felt (`horisontYPiksel`) som klikket
   * ville satt — auto-deteksjon er et alternativ til klikket, ikke et
   * parallelt datafelt (§3). Krever `vfovDeg` og øyehøyde for å regne
   * pitch/roll fra fittet; uten det settes `horisontYPiksel` fortsatt (nok
   * for `maritimAvstand`), men `horisontAuto` (pitch/roll-diagnostikken)
   * blir stående `null`.
   *
   * Ren funksjon av en `Pikselrute` — DOM-fri og testbar uten canvas/jsdom;
   * selve canvas-uthentingen er panelets (`MalpekingPanel.autoDetekterHorisont`)
   * jobb.
   */
  settHorisontFraAutoDeteksjon(bilde: Pikselrute): { kind: 'ok' | 'avvist'; forklaring: string } {
    const deteksjon = detekterHorisontLinje(bilde);
    if (deteksjon.kind === 'avvist') {
      this.horisontAuto = null;
      return { kind: 'avvist', forklaring: deteksjon.forklaring };
    }
    // Linja er funnet — dette ER referansen `maritimAvstand` trenger, uansett
    // om pitch/roll lar seg regne ut fra den (se merknaden over).
    this.horisontYPiksel = deteksjon.yVedSenterPx;

    const hoydeM = this.bakkehoydeM - this.vannstandM + this.holdehoydeM;
    const pitchRoll = pitchRollFraHorisontlinje(this.kalibrering(), deteksjon, hoydeM, {
      bolgehoydeSignifikantM: this.bolgehoydeSignifikantM,
    });
    if (pitchRoll.kind === 'avvist') {
      this.horisontAuto = null;
      return { kind: 'avvist', forklaring: `Horisontlinje funnet (y=${deteksjon.yVedSenterPx.toFixed(1)}px), men ${pitchRoll.forklaring}` };
    }
    this.horisontAuto = pitchRoll;
    return { kind: 'ok', forklaring: pitchRoll.forklaring };
  }

  /**
   * Regn ut alt: avstand (maritim), asimut (resection → magnetometer/kamera-
   * asimut som fallback), og fusjonen — ELLER en sektor når bare asimut er
   * kjent, ELLER `null`-feltene når ingenting er kjent. Aldri et bart tall.
   */
  beregn(): MalpekingBeregning {
    const advarsler: string[] = [];
    const kal = this.kalibrering();

    // ── Avstand: maritim horisont+havflate ────────────────────────────────
    let avstand: MaritimtResultat | null = null;
    if (this.horisontYPiksel !== null && this.malYPiksel !== null && this.vfovDeg !== undefined && this.hoydePx > 0) {
      const gapDeg = gapDegFraPiksler(this.vfovDeg, this.hoydePx, this.horisontYPiksel, this.malYPiksel);
      if (gapDeg !== null) {
        const hoydeM = this.bakkehoydeM - this.vannstandM + this.holdehoydeM;
        avstand = maritimAvstand({ hoydeM, gapDeg, bolgehoydeSignifikantM: this.bolgehoydeSignifikantM });
      }
    }

    // ── Asimut: resection først, kamera-/magnetometer-asimut som fallback ──
    let resection: ResectionResultat | null = null;
    let asimut: AsimutKomponent | null = null;
    if (this.landemerker.length > 0) {
      resection = losResection({
        kameraLat: this.kameraLat, kameraLon: this.kameraLon,
        breddePx: this.breddePx, hfovDeg: this.hfovDeg,
        landemerker: this.landemerker,
        magnetometerAsimutDeg: this.kameraAsimutDeg,
      });
      if (resection.kind === 'ok' && this.malXPiksel !== null) {
        const til = resectionPeilingTilMaal(resection, this.breddePx, this.hfovDeg, this.malXPiksel);
        asimut = { asimutDeg: til.asimutDeg, sigmaDeg: til.sigmaDeg, metode: resection.metode, forklaring: resection.forklaring, advarsler: resection.advarsler };
      }
    }
    if (!asimut && this.kameraAsimutDeg !== undefined && this.malXPiksel !== null) {
      const asimutDeg = peilingFraPiksel(kal, this.malXPiksel);
      const sigmaDeg = kameraSektorHalvbreddeDeg(kal);
      asimut = {
        asimutDeg, sigmaDeg, metode: 'kamera-asimut',
        forklaring:
          `Ingen landemerker identifisert — asimut fra oppgitt kamera-/magnetometer-asimut ` +
          `${this.kameraAsimutDeg.toFixed(0)}° ± ${sigmaDeg.toFixed(0)}°. Dette er ` +
          `${sigmaDeg >= MAGNETOMETER_SIGMA_DEG_DEFAULT ? 'på magnetometerets nivå' : 'bedre enn magnetometerets ±20°, men'} — ` +
          'legg til et landemerke for <0,1° presisjon (konseptdok funn 2).',
      };
    }

    let maal: Maalpeking | null = null;
    let sektor: MalpekingBeregning['sektor'] = null;
    if (asimut && avstand && avstand.kind === 'ok') {
      const avstandKomponent: AvstandKomponent = {
        avstandM: avstand.avstandM, sigmaM: avstand.sigmaM, metode: avstand.metode,
        forklaring: avstand.forklaring, advarsler: avstand.advarsler,
      };
      maal = fusjonerMaalpeking({ lat: this.kameraLat, lon: this.kameraLon }, asimut, avstandKomponent);
    } else if (asimut) {
      sektor = {
        asimutDeg: asimut.asimutDeg,
        halvbreddeDeg: Math.max(asimut.sigmaDeg, 0.5),
        metode: asimut.metode,
        forklaring:
          asimut.forklaring +
          (avstand && avstand.kind === 'avvist'
            ? ` Avstand IKKE beregnet: ${avstand.forklaring}`
            : ' Ingen avstand beregnet — klikk horisont OG mål for å låse den.'),
      };
      advarsler.push('Bare retning er kjent — tegnet som sektor, ikke som punkt.');
    } else {
      advarsler.push(
        'Ingen asimut kjent (verken landemerke eller kamera-/magnetometer-asimut oppgitt) — ' +
        'ingen posisjon eller sektor kan tegnes.',
      );
    }

    if (avstand && avstand.kind === 'ok') advarsler.push(...avstand.advarsler);
    if (resection && resection.kind === 'ok') advarsler.push(...resection.advarsler);

    return { avstand, resection, maal, sektor, advarsler };
  }
}

// ── Kart-tegning (rene GeoJSON-byggere, DOM-frie) ───────────────────────────

const SRC = 'malpeking';

export function malpekingFeatures(kamera: LatLon, b: MalpekingBeregning): GeoFeature[] {
  const feats: GeoFeature[] = [
    { type: 'Feature', geometry: { type: 'Point', coordinates: [kamera.lon, kamera.lat] }, properties: { rolle: 'kamera' } },
  ];
  if (b.maal) {
    feats.push({ type: 'Feature', geometry: { type: 'Point', coordinates: [b.maal.lon, b.maal.lat] }, properties: { rolle: 'maal' } });
    const ring = treffusikkerhetRing({ lat: b.maal.lat, lon: b.maal.lon }, b.maal.usikkerhet);
    if (ring.length > 2) {
      feats.push({ type: 'Feature', geometry: { type: 'Polygon', coordinates: [ring] }, properties: { rolle: 'ellipse' } });
    }
  } else if (b.sektor) {
    const rMax = KAMERA_REKKEVIDDE_DEFAULT_M * 10; // tegnelengde — ingen avstand er kjent, se forklaringen
    const ring = sektorPolygon(kamera, b.sektor.asimutDeg, b.sektor.halvbreddeDeg, rMax);
    feats.push({ type: 'Feature', geometry: { type: 'Polygon', coordinates: [ring] }, properties: { rolle: 'sektor' } });
  }
  return feats;
}

/** Senter for kartvinduet: målet om det finnes, ellers et punkt langs sektoren, ellers kameraet selv. */
export function malpekingKartsenter(kamera: LatLon, b: MalpekingBeregning): LatLon {
  if (b.maal) return { lat: b.maal.lat, lon: b.maal.lon };
  if (b.sektor) return project(kamera, b.sektor.asimutDeg, KAMERA_REKKEVIDDE_DEFAULT_M * 3);
  return kamera;
}

// ── Panelet (DOM) ────────────────────────────────────────────────────────────

export interface MalpekingHooks {
  center?: () => { lon: number; lat: number };
  alarm?: (tekst: string) => void;
}

export class MalpekingPanel {
  readonly el: HTMLElement;
  readonly tilstand = new MalpekingTilstand();
  onStateChange?: () => void;
  onEndring?: () => void;

  private open = false;
  private ready = false;
  private siste: MalpekingBeregning | null = null;

  private imgEl!: HTMLImageElement;
  private overlayEl!: HTMLElement;
  private landemerkeListeEl!: HTMLElement;
  private resultEl!: HTMLElement;
  private statusEl!: HTMLElement;

  get apen(): boolean { return this.open; }

  constructor(private map: MlMap, private hooks: MalpekingHooks = {}) {
    this.el = this.build();
    if ((map as { loaded?: () => boolean }).loaded?.()) this.init();
    else map.on?.('load', () => this.init());
  }

  toggle(): void {
    this.open = !this.open;
    this.el.classList.toggle('open', this.open);
    this.onStateChange?.();
  }
  erApen(): boolean { return this.open; }
  sisteResultat(): MalpekingBeregning | null { return this.siste; }

  nullstill(): void {
    this.tilstand.nullstill();
    this.siste = null;
    this.render();
    this.tegn(null);
    this.onEndring?.();
  }

  /** Regn ut, vis resultatet, og sentrer/tegn i kartet — «kartvinduet». */
  beregnOgVis(): void {
    const b = this.tilstand.beregn();
    this.siste = b;
    this.visResultat(b);
    const kamera: LatLon = { lat: this.tilstand.kameraLat, lon: this.tilstand.kameraLon };
    this.tegn(b);
    if (b.maal || b.sektor) {
      const senter = malpekingKartsenter(kamera, b);
      try { this.map.flyTo?.({ center: [senter.lon, senter.lat], zoom: Math.max(this.map.getZoom?.() ?? 12, 12) }); } catch { /* kart uten flyTo (test) */ }
    }
    this.onEndring?.();
  }

  // ── Kartlag ───────────────────────────────────────────────────────────────

  private init(): void {
    if (this.ready) return;
    try {
      if (!this.map.getSource(SRC)) {
        this.map.addSource(SRC, { type: 'geojson', data: featureCollection([]) as never });
        this.map.addLayer({
          id: 'malpeking-ellipse', type: 'fill', source: SRC,
          filter: ['==', ['get', 'rolle'], 'ellipse'],
          paint: { 'fill-color': '#d4a03a', 'fill-opacity': 0.18 },
        });
        this.map.addLayer({
          id: 'malpeking-sektor', type: 'fill', source: SRC,
          filter: ['==', ['get', 'rolle'], 'sektor'],
          paint: { 'fill-color': '#8b9b8b', 'fill-opacity': 0.14 },
        });
        this.map.addLayer({
          id: 'malpeking-punkt', type: 'circle', source: SRC,
          filter: ['==', ['geometry-type'], 'Point'],
          paint: {
            'circle-radius': ['match', ['get', 'rolle'], 'maal', 6, 4],
            'circle-color': ['match', ['get', 'rolle'], 'maal', '#d4a03a', '#8ba888'],
            'circle-stroke-color': '#0a0c0a', 'circle-stroke-width': 2,
          },
        });
      }
      this.ready = true;
    } catch { /* kart uten lag-API (test) */ }
  }

  private tegn(b: MalpekingBeregning | null): void {
    this.init();
    const kamera: LatLon = { lat: this.tilstand.kameraLat, lon: this.tilstand.kameraLon };
    const feats = b ? malpekingFeatures(kamera, b) : [];
    try {
      (this.map.getSource(SRC) as GeoJSONSource | undefined)?.setData(featureCollection(feats) as never);
    } catch { /* ingen kilde (test) */ }
  }

  // ── DOM ───────────────────────────────────────────────────────────────────

  private build(): HTMLElement {
    const p = div('report-composer malpeking-panel');
    const head = div('report-head');
    head.append(span('report-title', 'Målpeking — pek i kamerabildet'));
    const x = document.createElement('button');
    x.className = 'report-x'; x.type = 'button'; x.title = 'Lukk';
    x.textContent = '×'; x.onclick = () => this.toggle();
    head.append(x);
    p.append(head);

    p.append(this.bildeFelt());

    const stage = div('imint-stage malpeking-stage');
    this.imgEl = document.createElement('img');
    this.imgEl.className = 'imint-img';
    this.imgEl.alt = 'Kamerabilde';
    this.overlayEl = div('imint-overlay malpeking-overlay');
    this.overlayEl.onclick = (e) => this.onBildeKlikk(e);
    this.imgEl.onload = () => {
      this.tilstand.breddePx = this.imgEl.naturalWidth;
      this.tilstand.hoydePx = this.imgEl.naturalHeight;
      this.tegnMarkorer();
    };
    stage.append(this.imgEl, this.overlayEl);
    p.append(stage);

    const modusRad = div('malpeking-knapper');
    modusRad.append(
      this.modusKnapp('horisont', 'Sett horisont', 'Klikk i bildet: horisontlinja'),
      this.modusKnapp('mal', 'Sett mål', 'Klikk i bildet: målets vannlinje/fotpunkt'),
      this.modusKnapp('landemerke', 'Legg til landemerke', 'Klikk i bildet: et kjent, identifisert punkt'),
      knapp(
        'Auto-detekter horisont',
        'Finn horisontlinja automatisk i bildet (linjefit i pikslene) i stedet for å klikke — fyller samme felt, og gir pitch/roll langt bedre enn IMU-en',
        () => this.autoDetekterHorisont(),
      ),
    );
    p.append(modusRad);

    p.append(this.kalibreringForm());
    p.append(this.oyehoydeForm());

    this.landemerkeListeEl = div('malpeking-landemerker');
    p.append(this.landemerkeListeEl);

    const knapper = div('sikt-knapper');
    const regn = document.createElement('button');
    regn.type = 'button'; regn.className = 'report-submit';
    regn.textContent = 'Regn ut og vis i kart';
    regn.onclick = () => this.beregnOgVis();
    knapper.append(regn);
    const nullstillBtn = knapp('Nullstill', 'Fjern klikk, landemerker og resultat', () => this.nullstill());
    knapper.append(nullstillBtn);
    p.append(knapper);

    this.statusEl = div('sikt-status');
    this.statusEl.textContent = 'Legg til et bilde, sett horisont/mål, og regn ut.';
    p.append(this.statusEl);

    this.resultEl = div('malpeking-resultat');
    p.append(this.resultEl);

    const note = div('sikt-note');
    note.textContent =
      'Landemerker identifiseres MANUELT (ikke bildegjenkjenning). Horisontmetoden bruker ' +
      'ingen høydemodell — bare horisont- og vannlinjegeometri. «Auto-detekter horisont» finner ' +
      'linja i pikslene i stedet for et klikk, og gir pitch/roll fra selve bildet — langt bedre ' +
      'enn telefonens IMU over vann. Vis ALDRI ett tall: alltid avstand/asimut + σ + metode.';
    p.append(note);

    this.render();
    return p;
  }

  private bildeFelt(): HTMLElement {
    const f = div('report-field');
    f.append(span('report-field-label', 'Bilde-URL (https:// eller /media/…)'));
    const inp = document.createElement('input');
    inp.type = 'text'; inp.className = 'report-input';
    inp.placeholder = 'https://… eller /media/…';
    inp.onchange = () => {
      this.tilstand.bildeUrl = inp.value;
      this.imgEl.src = trygtBildeUrl(inp.value);
    };
    f.append(inp);
    return f;
  }

  private modusKnapper: [MalpekingKlikkModus, HTMLButtonElement][] = [];

  private modusKnapp(modus: MalpekingKlikkModus, etikett: string, tittel: string): HTMLButtonElement {
    const b = knapp(etikett, tittel, () => {
      this.tilstand.modus = this.tilstand.modus === modus ? 'ingen' : modus;
      this.render();
    });
    b.classList.add('malpeking-modus-btn');
    // ⚠ IKKE `this.el.querySelectorAll(...)` for å finne disse igjen i render():
    // build() kaller render() FØR `this.el = this.build()` har rukket å tilordne
    // this.el — et oppslag på this.el her ville truffet `undefined`, ikke
    // knappene. Referansene lagres derfor direkte, samme instinkt som førte til
    // at `onEndring`/`onStateChange` er egne felt og ikke DOM-oppslag.
    this.modusKnapper.push([modus, b]);
    return b;
  }

  private onBildeKlikk(e: MouseEvent): void {
    if (this.tilstand.modus === 'ingen') return;
    const r = this.overlayEl.getBoundingClientRect();
    const xNorm = clamp01((e.clientX - r.left) / r.width);
    const yNorm = clamp01((e.clientY - r.top) / r.height);
    const modus = this.tilstand.modus;
    this.tilstand.settKlikkNormalisert(xNorm, yNorm);
    if (modus === 'landemerke') {
      this.sporLandemerke();
    }
    this.render();
    this.tegnMarkorer();
  }

  /**
   * Kort MAAL-03: finn horisontlinja automatisk i stedet for å klikke den.
   * Leser bildets pikslene via en FRAKOBLET <canvas> (aldri lagt til DOM-en,
   * kastes etter kallet) — samme adaptermønster som `map/dekning.ts` og
   * `map/skygge.ts` bruker for RGBA-bilder. Selve algoritmen bor i
   * @skade/contracts og er DOM-fri; denne funksjonen er BARE pikseladapteren.
   */
  private autoDetekterHorisont(): void {
    const t = this.tilstand;
    if (!t.breddePx || !t.hoydePx) {
      this.hooks.alarm?.('Auto-deteksjon: last et bilde først.');
      return;
    }
    let bilde: Pikselrute;
    try {
      const canvas = document.createElement('canvas');
      canvas.width = t.breddePx;
      canvas.height = t.hoydePx;
      const ctx = canvas.getContext('2d');
      if (!ctx) {
        this.hooks.alarm?.('Auto-deteksjon: canvas 2D-kontekst er ikke tilgjengelig i denne nettleseren.');
        return;
      }
      ctx.drawImage(this.imgEl, 0, 0, t.breddePx, t.hoydePx);
      const data = ctx.getImageData(0, 0, t.breddePx, t.hoydePx);
      bilde = { breddePx: data.width, hoydePx: data.height, data: data.data };
    } catch {
      // `drawImage`/`getImageData` kaster (SecurityError) hvis bildekilden er
      // cross-origin uten CORS-headere — en ærlig feilmelding i stedet for at
      // knappen bare gjør ingenting synlig.
      this.hooks.alarm?.('Auto-deteksjon: kunne ikke lese bildepiksler (mulig CORS-blokkering av bildekilden).');
      return;
    }
    const status = t.settHorisontFraAutoDeteksjon(bilde);
    if (status.kind === 'avvist') {
      this.hooks.alarm?.(`Auto-deteksjon avvist: ${status.forklaring}`);
    }
    this.render();
    this.tegnMarkorer();
  }

  /** Tre korte prompt(er) for navn/lat/lon — samme `sporTekst`-mønster som imint.ts bruker for merkelapp. */
  private sporLandemerke(): void {
    sporTekst({
      tittel: 'Nytt landemerke', tekst: 'Navn (fyrlykt, mast, kai …)',
      onSvar: (navn) => {
        sporTekst({
          tittel: 'Landemerkets posisjon', tekst: 'Breddegrad (lat)',
          onSvar: (latStr) => {
            sporTekst({
              tittel: 'Landemerkets posisjon', tekst: 'Lengdegrad (lon)',
              onSvar: (lonStr) => {
                const lat = Number(latStr), lon = Number(lonStr);
                if (Number.isFinite(lat) && Number.isFinite(lon)) {
                  this.tilstand.leggTilLandemerke((navn || '').trim() || 'Landemerke', lat, lon);
                } else {
                  this.tilstand.ventendeLandemerkeXPiksel = null;
                  this.hooks.alarm?.('Landemerke IKKE lagt til — lat/lon var ikke gyldige tall.');
                }
                this.render();
              },
            });
          },
        });
      },
    });
  }

  private kalibreringForm(): HTMLElement {
    const f = div('report-form');
    f.append(
      this.tallFelt('Kamera lat', () => this.tilstand.kameraLat, (v) => { this.tilstand.kameraLat = v; }, -90, 90, 0.0001),
      this.tallFelt('Kamera lon', () => this.tilstand.kameraLon, (v) => { this.tilstand.kameraLon = v; }, -180, 180, 0.0001),
      this.tallFelt('Horisontalt synsfelt (hfov, °)', () => this.tilstand.hfovDeg, (v) => { this.tilstand.hfovDeg = v; }, 1, 180, 0.1),
      this.tallFeltValgfri('Vertikalt synsfelt (vfov, °) — kreves for horisontmetoden', () => this.tilstand.vfovDeg, (v) => { this.tilstand.vfovDeg = v; }, 0, 180, 0.1),
      this.tallFeltValgfri('Kamera-/magnetometer-asimut (° sann, deklinasjonskorrigert)', () => this.tilstand.kameraAsimutDeg, (v) => { this.tilstand.kameraAsimutDeg = v; }, 0, 360, 1),
      this.tallFeltValgfri('σ kamera-asimut (° — tom = halve synsfeltet)', () => this.tilstand.kameraAsimutSigmaDeg, (v) => { this.tilstand.kameraAsimutSigmaDeg = v; }, 0, 180, 0.5),
    );
    return f;
  }

  private oyehoydeForm(): HTMLElement {
    const f = div('report-form');
    f.append(
      this.tallFelt('Bakke/kai over datum (m)', () => this.tilstand.bakkehoydeM, (v) => { this.tilstand.bakkehoydeM = v; }, -5, 500, 0.1),
      this.tallFelt('Vannstand nå (m, over samme datum)', () => this.tilstand.vannstandM, (v) => { this.tilstand.vannstandM = v; }, -3, 5, 0.01),
      this.tallFelt('Holdehøyde (m)', () => this.tilstand.holdehoydeM, (v) => { this.tilstand.holdehoydeM = v; }, 0, 5, 0.05),
      this.tallFeltValgfri('Signifikant bølgehøyde Hs (m) — hvis kjent', () => this.tilstand.bolgehoydeSignifikantM, (v) => { this.tilstand.bolgehoydeSignifikantM = v; }, 0, 10, 0.1),
    );
    return f;
  }

  private tallFelt(etikett: string, les: () => number, skriv: (v: number) => void, min: number, max: number, steg: number): HTMLElement {
    const f = div('report-field');
    f.append(span('report-field-label', etikett));
    const inp = document.createElement('input');
    inp.type = 'number'; inp.className = 'report-input';
    inp.min = String(min); inp.max = String(max); inp.step = String(steg);
    inp.value = String(les());
    inp.onchange = () => { const v = Number(inp.value); if (Number.isFinite(v)) skriv(Math.min(max, Math.max(min, v))); };
    f.append(inp);
    return f;
  }

  private tallFeltValgfri(
    etikett: string, les: () => number | undefined, skriv: (v: number | undefined) => void,
    min: number, max: number, steg: number,
  ): HTMLElement {
    const f = div('report-field');
    f.append(span('report-field-label', etikett));
    const inp = document.createElement('input');
    inp.type = 'number'; inp.className = 'report-input';
    inp.min = String(min); inp.max = String(max); inp.step = String(steg);
    inp.placeholder = 'ikke oppgitt';
    const v0 = les();
    inp.value = v0 === undefined ? '' : String(v0);
    inp.onchange = () => {
      if (inp.value.trim() === '') { skriv(undefined); return; }
      const v = Number(inp.value);
      if (Number.isFinite(v)) skriv(Math.min(max, Math.max(min, v)));
    };
    f.append(inp);
    return f;
  }

  private render(): void {
    this.landemerkeListeEl.replaceChildren();
    for (const lm of this.tilstand.landemerker) {
      const rad = div('malpeking-landemerke-rad');
      rad.append(span('malpeking-landemerke-navn', `${lm.navn} · x=${Math.round(lm.xPiksel)}px`));
      const slett = document.createElement('button');
      slett.type = 'button'; slett.className = 'sikt-btn';
      slett.textContent = 'Fjern';
      slett.onclick = () => { this.tilstand.fjernLandemerke(lm.id); this.render(); this.tegnMarkorer(); };
      rad.append(slett);
      this.landemerkeListeEl.append(rad);
    }
    if (this.tilstand.ventendeLandemerkeXPiksel !== null) {
      const p = div('malpeking-landemerke-rad');
      p.textContent = `Venter på navn/posisjon for klikket ved x=${Math.round(this.tilstand.ventendeLandemerkeXPiksel)}px …`;
      this.landemerkeListeEl.append(p);
    }
    for (const [modus, b] of this.modusKnapper) {
      b.classList.toggle('aktiv', modus === this.tilstand.modus);
    }

    const deler: string[] = [];
    if (this.tilstand.horisontYPiksel !== null) deler.push('horisont satt');
    if (this.tilstand.malYPiksel !== null) deler.push('mål satt');
    if (this.tilstand.landemerker.length > 0) deler.push(`${this.tilstand.landemerker.length} landemerke(r)`);
    this.statusEl.textContent = deler.length > 0 ? deler.join(' · ') : 'Legg til et bilde, sett horisont/mål, og regn ut.';
  }

  /** Tegn horisontlinje/mål/landemerker som enkle markører over bildet. */
  private tegnMarkorer(): void {
    this.overlayEl.querySelectorAll('.malpeking-marker').forEach((n) => n.remove());
    const t = this.tilstand;
    if (!t.breddePx || !t.hoydePx) return;
    if (t.horisontYPiksel !== null) {
      const m = div('malpeking-marker malpeking-marker-horisont');
      m.style.top = `${(t.horisontYPiksel / t.hoydePx) * 100}%`;
      this.overlayEl.append(m);
    }
    if (t.malXPiksel !== null && t.malYPiksel !== null) {
      const m = div('malpeking-marker malpeking-marker-mal');
      m.style.left = `${(t.malXPiksel / t.breddePx) * 100}%`;
      m.style.top = `${(t.malYPiksel / t.hoydePx) * 100}%`;
      this.overlayEl.append(m);
    }
    for (const lm of t.landemerker) {
      const m = div('malpeking-marker malpeking-marker-landemerke');
      m.style.left = `${(lm.xPiksel / t.breddePx) * 100}%`;
      m.title = lm.navn;
      this.overlayEl.append(m);
    }
  }

  private visResultat(b: MalpekingBeregning): void {
    this.resultEl.replaceChildren();
    const rad = (k: string, v: string) => {
      const r = div('report-kv');
      r.append(span('report-k', k), span('report-v', v));
      this.resultEl.append(r);
    };
    if (b.avstand?.kind === 'ok') {
      rad('Avstand', `${Math.round(b.avstand.avstandM)} m ± ${Math.round(b.avstand.sigmaM)} m · ${b.avstand.metode}`);
    } else if (b.avstand?.kind === 'avvist') {
      rad('Avstand', `IKKE beregnet — ${b.avstand.forklaring}`);
    }
    if (b.resection?.kind === 'ok') {
      rad('Asimut (resection)', `${b.resection.asimutDeg.toFixed(2)}° ± ${b.resection.sigmaDeg.toFixed(2)}° · ${b.resection.antallLandemerker} landemerke(r)`);
      if (b.resection.magnetometer) {
        rad('Magnetometer', `${b.resection.magnetometer.asimutDeg.toFixed(0)}° ± ${b.resection.magnetometer.sigmaDeg.toFixed(0)}° (avvik ${b.resection.magnetometer.avvikDeg.toFixed(1)}°)`);
      }
    }
    if (this.tilstand.horisontAuto?.kind === 'ok') {
      const hr = this.tilstand.horisontAuto;
      rad(
        'Horisont auto-fit (pitch/roll)',
        `pitch ${hr.pitchDeg.toFixed(2)}° ± ${hr.pitchSigmaDeg.toFixed(2)}° · roll ${hr.rollDeg.toFixed(2)}° ± ${hr.rollSigmaDeg.toFixed(2)}° ` +
        '— langt under IMU-ens 1,7° (maalestimat.ts)',
      );
    }
    if (b.maal) {
      rad('Målposisjon', `${b.maal.lat.toFixed(5)}, ${b.maal.lon.toFixed(5)} · CEP ${Math.round(b.maal.usikkerhet.cepM)} m`);
    } else if (b.sektor) {
      rad('Sektor (avstand ukjent)', `${b.sektor.asimutDeg.toFixed(1)}° ± ${b.sektor.halvbreddeDeg.toFixed(1)}° · ${b.sektor.metode}`);
    }
    for (const a of b.advarsler) {
      const el2 = div('sikt-advarsel');
      el2.textContent = a;
      this.resultEl.append(el2);
    }
    if (!b.maal && !b.sektor) this.hooks.alarm?.('Målpeking: ingen posisjon eller sektor kunne beregnes — se forklaringen i panelet.');
  }
}

// Bilde-URL-guard: `trygtBildeUrl` (importert fra shell.ts over) er SAMME
// regel som `_testSafeImageUrl` i imint.ts (allerede låst enige av
// `bildevisning.test.ts`) — ingen tredje kopi av regex-en her.

function clamp01(n: number): number { return Math.max(0, Math.min(1, n)); }
function div(cls: string): HTMLElement { const e = document.createElement('div'); e.className = cls; return e; }
function span(cls: string, t: string): HTMLElement { const e = document.createElement('span'); e.className = cls; e.textContent = t; return e; }
function knapp(etikett: string, tittel: string, run: () => void): HTMLButtonElement {
  const b = document.createElement('button');
  b.type = 'button'; b.className = 'sikt-btn'; b.title = tittel;
  b.textContent = etikett; b.onclick = run;
  return b;
}
